import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MarketingFooter from '../components/layout/MarketingFooter';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, FileText, Headphones, PenLine, Lock, X } from 'lucide-react';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import examService from '../services/examService';
import { parseExamSlug } from '../utils/examSlug';
import { trackConversionEvent } from '../utils/conversionEvents';
import { saveGuestExamPending, loadGuestExamPending, clearGuestExamPending } from '../utils/onboardingStorage';
import { adoptAnonExamSession } from '../utils/sessionStore.js';
import ExamSimulatorPage from '../components/practice/ExamSimulatorPage.jsx';
import LogamiLogoSpinner from '../components/ui/LogamiLogoSpinner';
import ExamModePicker from '../components/practice/ExamModePicker.jsx';
import { PAPER_LEVELS } from '../components/paperExam/paperLevels';
import { examPapers } from '../data/mockExamPapers';
import storageService from '../services/storageService';
import { supportsPaper, paperSlugFor, getExamModePreference, setExamModePreference } from '../utils/examMode';

// The paper exam (booklet view) loads only when a visitor sits one.
const TelcPaperSitting = lazy(() => import('../components/paperExam/telc/TelcPaperSitting.jsx'));

const PROVIDERS = [
  { id: 'TELC', label: 'TELC' },
  { id: 'Goethe', label: 'Goethe' },
  { id: 'OSD', label: 'ÖSD' },
];
const LEVELS = ['B1', 'B2', 'C1'];

/**
 * Public guest mock-exam flow (no account needed):
 * homepage "Try a free exam" CTA → provider/level select → full exam
 * simulator (anonymous session, saved on-device) → score teaser + signup CTA.
 * Deep-linkable via /try?exam=telc-b2 for SEO/ads landing pages.
 */
const TryExamPage = ({ user, isAuthenticated }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const [searchParams] = useSearchParams();

  const preselect = parseExamSlug(searchParams.get('exam'));
  const [provider, setProvider] = useState(preselect?.provider === 'OSD' ? 'OSD' : preselect?.provider === 'GOETHE' ? 'Goethe' : preselect ? 'TELC' : null);
  const [level, setLevel] = useState(preselect?.level || null);
  const [step, setStep] = useState('select'); // 'select' | 'loading' | 'exam' | 'results'
  const [examData, setExamData] = useState(null);
  const [results, setResults] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // uid the running exam is bound to (null = anonymous). Set only after the
  // anon→uid session migration, so the simulator never remounts onto the uid
  // key before the snapshot copy exists. Also the simulator's `key`.
  const [resumeUid, setResumeUid] = useState(null);
  // Paper (default) or digital; `runMode` is the mode of the exam being sat.
  const [mode, setMode] = useState(getExamModePreference);
  const [runMode, setRunMode] = useState('digital');
  const [paperConfig, setPaperConfig] = useState(null);
  const paperOk = supportsPaper(provider, level);
  const chooseMode = (next) => { setMode(next); setExamModePreference(next, user?.uid || null); };

  useEffect(() => {
    const prev = document.title;
    document.title = t('tryExam.pageTitle', { defaultValue: 'Free German Mock Exam. TELC, Goethe & ÖSD | Logami' });
    return () => { document.title = prev; };
  }, [t]);

  const startExam = useCallback(async () => {
    if (!provider || !level) return;
    setStep('loading');
    setLoadError(null);
    // Already-signed-in visitors run uid-bound from the start (no gate, no migration).
    setResumeUid(user?.uid || null);
    const chosen = paperOk ? mode : 'digital';
    setRunMode(chosen);
    trackConversionEvent('guest_exam_start', { exam_provider: provider, exam_level: level, exam_mode: chosen });
    try {
      const data = await examService.loadGuestExam(provider, level);
      setExamData(data);
      setStep('exam');
    } catch (error) {
      console.error('Guest exam load failed:', error);
      setLoadError(error.message);
      setStep('select');
    }
  }, [provider, level, user?.uid, paperOk, mode]);

  const handleFinishExam = useCallback((finalAnswers, flaggedQuestions, timeSpent = 0) => {
    const score = examService.calculateFinalScore(examData, finalAnswers);
    setResults({ ...score, timeSpent });
    setStep('results');
    // The exam is done: retire the dashboard "Continue your exam" banner.
    clearGuestExamPending();
    trackConversionEvent('guest_exam_complete', {
      exam_provider: provider,
      exam_level: level,
      score_pct: Math.round(score.overallScore || 0),
    });
    window.scrollTo(0, 0);
  }, [examData, provider, level]);

  const goSignup = useCallback((ctaName) => {
    trackConversionEvent('guest_results_signup_click', { cta_name: ctaName, exam_provider: provider, exam_level: level });
    navigate('/auth?mode=signup');
  }, [navigate, provider, level]);

  // "Sign up with e-mail instead" in the gate modal: mark the exam as pending,
  // then go through the normal signup → onboarding → dashboard journey. The
  // dashboard shows a "Continue your exam" banner (from the pending marker)
  // that leads back here, where the resume effect below picks it up.
  const handleGuestGate = useCallback(() => {
    trackConversionEvent('guest_exam_gate_more_options', { exam_provider: provider, exam_level: level });
    saveGuestExamPending({ provider, level, mode: runMode });
    navigate('/auth?mode=signup');
  }, [provider, level, runMode, navigate]);

  // Google popup succeeded inside the gate modal. Same journey as e-mail:
  // secure the session under the new uid, mark the exam pending, then send the
  // fresh account through onboarding. The dashboard's Continue banner brings
  // them back to finish Hören/Schreiben.
  const handleGuestAuthed = useCallback((firebaseUser) => {
    if (firebaseUser?.uid && examData) {
      adoptAnonExamSession(firebaseUser.uid, examData);
      // Deliberately NOT marking the free attempt here. The attempt is consumed
      // by COMPLETING an exam, and this user was interrupted at the Hören gate
      // mid-paper. Marking now would mean the exam they are about to resume
      // could no longer be saved (firestore.rules refuses the create once the
      // flag exists) — they would finish and lose the result.
    }
    saveGuestExamPending({ provider, level, mode: runMode });
    trackConversionEvent('guest_exam_gate_signup_success', { exam_provider: provider, exam_level: level, method: 'popup' });
    navigate('/onboarding');
  }, [provider, level, examData, runMode, navigate]);

  // Resume a pending guest exam: an authenticated user arriving here (usually
  // via the dashboard's "Continue your exam" banner) with an unfinished gate
  // exam goes straight back into it. Non-destructive: the pending marker is
  // only cleared when the exam is actually finished.
  useEffect(() => {
    if (!isAuthenticated || !user?.uid || step !== 'select' || examData) return;
    const pending = loadGuestExamPending(user.uid);
    if (!pending) return;
    setProvider(pending.provider);
    setLevel(pending.level);
    // Back into the mode the exam was started in.
    setRunMode(pending.mode === 'paper' && supportsPaper(pending.provider, pending.level) ? 'paper' : 'digital');
    setStep('loading');
    (async () => {
      try {
        const data = await examService.loadGuestExam(pending.provider, pending.level);
        adoptAnonExamSession(user.uid, data);
        // Same reasoning as handleGuestAuthed: resuming is not completing.
        setExamData(data);
        setResumeUid(user.uid);
        setStep('exam');
        trackConversionEvent('guest_exam_resumed_after_signup', { exam_provider: pending.provider, exam_level: pending.level });
      } catch (error) {
        console.error('Guest exam resume failed:', error);
        setLoadError(error.message);
        setStep('select');
      }
    })();
  }, [isAuthenticated, user?.uid, step, examData]); // eslint-disable-line react-hooks/exhaustive-deps

  // The paper exam's booklet config, loaded when a paper sitting starts.
  const paperEntry = runMode === 'paper' && provider && level ? PAPER_LEVELS[paperSlugFor(provider, level)] : null;
  useEffect(() => {
    if (!paperEntry) return undefined;
    let cancelled = false;
    paperEntry.load().then((mod) => { if (!cancelled) setPaperConfig(mod.default); });
    return () => { cancelled = true; };
  }, [paperEntry]);

  if (step === 'exam' && examData && paperEntry) {
    // Position among the sittable papers (the cover's and sheet's version number, LGM-…-01).
    const sittable = examPapers(examData.examProvider, examData.level).filter((p) => !p.comingSoon);
    const paperIndex = Math.max(0, sittable.findIndex((p) => p.file === examData.examFileName));
    const listed = sittable[paperIndex]?.file === examData.examFileName ? sittable[paperIndex] : null;
    const paper = listed ? { ...listed, index: paperIndex } : {
      index: 0,
      id: String(examData.examFileName || examData.examId).replace(/\.json$/, ''),
      name: examData.variantName || '',
      file: examData.examFileName,
    };
    const spinner = (
      <div className="flex items-center justify-center min-h-screen bg-[#525659]">
        <LogamiLogoSpinner size="hero" decorative={false} />
      </div>
    );
    if (!paperConfig) return spinner;
    return (
      <Suspense fallback={spinner}>
        <TelcPaperSitting
          key={`${resumeUid || user?.uid || 'anon'}-${paper.id}`}
          uid={resumeUid || user?.uid || null}
          level={paperConfig}
          exam={examData}
          paper={paper}
          title={`${paperEntry.label} · Übungstest ${paper.name}`}
          shortTitle={`Übungstest ${paper.name}`}
          audioPath={storageService.generateAudioPath(examData.examProvider, String(examData.level).toUpperCase(), 'listening', paper.file, paper.name)}
          onExit={() => setStep('select')}
          onFinished={(finalAnswers, meta = {}) => handleFinishExam(finalAnswers, [], meta.timeSpent || 0)}
          guest={!user?.uid}
          guestGate={{ onGuestGate: handleGuestGate, onGuestAuthed: handleGuestAuthed }}
        />
      </Suspense>
    );
  }

  if (step === 'exam' && examData) {
    // Pass the real user whenever one is signed in, not only while resuming.
    // This prop was `resumeUid ? user : null`, so any logged-in free user who
    // opened /try rendered the simulator as an anonymous guest and skipped the
    // free-attempt check entirely — an unlimited-exam hole. guestGateActive
    // keys off !user?.uid, so a signed-in user correctly stops seeing the
    // "sign up to continue" modal.
    return (
      <ExamSimulatorPage
        key={resumeUid || user?.uid || 'anon'}
        user={user?.uid ? user : null}
        examData={examData}
        onFinishExam={handleFinishExam}
        onBack={() => setStep('select')}
        onGuestGate={handleGuestGate}
        onGuestAuthed={handleGuestAuthed}
      />
    );
  }

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div className="flex items-center justify-center h-20 w-20 rounded-2xl bg-white shadow-md ring-1 ring-stone-100">
            <LogamiLogoSpinner size="hero" decorative={false} alt={t('tryExam.loading', { defaultValue: 'Loading your exam' })} />
          </div>
        </div>
      </div>
    );
  }

  // Onboarding-style shell: warm backdrop with soft color blobs, dark blurred
  // overlay, one centered card, the same presentation as the onboarding modal.
  return (
    <div className="min-h-screen bg-[#FAFAF8] relative overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-amber-200/50 blur-3xl" />
        <div className="absolute top-1/3 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#3A86FF]/20 blur-3xl" />
        <div className="absolute -bottom-32 left-1/4 w-96 h-96 rounded-full bg-teal-200/40 blur-3xl" />
      </div>
      <div
        className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center px-3 py-6 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-label={t('tryExam.title', { defaultValue: 'Try a full mock exam' })}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          className="relative w-full max-w-lg my-auto bg-white border border-black/[0.05] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.25)] rounded-2xl sm:rounded-3xl max-h-[92vh] overflow-y-auto"
        >
          <div className="relative px-6 sm:px-8 pt-6 pb-7">
            <button
              type="button"
              onClick={() => navigate('/')}
              aria-label={t('tryExam.exitCta', { defaultValue: 'Exit' })}
              className="absolute top-4 right-4 inline-flex min-h-[48px] min-w-[48px] items-center justify-center rounded-full border border-stone-200 bg-white text-stone-500 shadow-sm transition-all duration-200 hover:border-stone-400 hover:text-stone-800 hover:shadow-md touch-manipulation"
            >
              <X className="w-4 h-4" />
            </button>
            <button onClick={() => navigate('/')} className="cursor-pointer block" aria-label="Logami">
              <img src="/logo.png" alt="logami" className="h-16 w-auto object-contain -my-3 mb-1" />
            </button>
        {step === 'select' && (
          <div className="animate-fade-in-up">
            <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-amber-600 mb-2">
              {t('tryExam.badge', { defaultValue: 'Free. No account needed.' })}
            </p>
            <h1
              className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tighter mb-3"
              style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}
            >
              {t('tryExam.title', { defaultValue: 'Try a full mock exam' })}
            </h1>
            <p className="text-sm sm:text-[15px] text-stone-500 leading-relaxed mb-7">
              {t('tryExam.subtitle', { defaultValue: 'The real exam format with authentic timing. Pick your exam and level. Your progress is saved on this device, so you can leave and come back anytime.' })}
            </p>

            {/* Provider */}
            <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-3">
              {t('tryExam.chooseProvider', { defaultValue: '1 · Choose your exam' })}
            </p>
            <div className="grid grid-cols-3 gap-3 max-w-lg mb-8">
              {PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setProvider(p.id)}
                  aria-pressed={provider === p.id}
                  className={`min-h-[64px] rounded-2xl border-2 font-bold text-base sm:text-lg transition-all duration-200 touch-manipulation cursor-pointer ${
                    provider === p.id
                      ? 'border-stone-900 bg-stone-900 text-white shadow-[0_4px_16px_rgba(28,25,23,0.2)]'
                      : 'border-stone-200 bg-white text-stone-700 hover:border-stone-400'
                  }`}
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Level */}
            <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-3">
              {t('tryExam.chooseLevel', { defaultValue: '2 · Choose your level' })}
            </p>
            <div className="grid grid-cols-3 gap-3 max-w-lg mb-10">
              {LEVELS.map((l) => (
                <button
                  key={l}
                  onClick={() => setLevel(l)}
                  aria-pressed={level === l}
                  className={`min-h-[52px] rounded-2xl border-2 font-bold transition-all duration-200 touch-manipulation cursor-pointer ${
                    level === l
                      ? 'border-stone-900 bg-stone-900 text-white shadow-[0_4px_16px_rgba(28,25,23,0.2)]'
                      : 'border-stone-200 bg-white text-stone-700 hover:border-stone-400'
                  }`}
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  {l}
                </button>
              ))}
            </div>

            {paperOk && (
              <>
                <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-3">
                  {t('tryExam.chooseMode')}
                </p>
                <ExamModePicker value={mode} onChange={chooseMode} compact stacked className="max-w-2xl mb-10" />
              </>
            )}

            {loadError && (
              <p className="text-sm text-red-600 mb-4" role="alert">
                {t('tryExam.loadError', { defaultValue: 'Could not load this exam. Please try another combination.' })}
              </p>
            )}

            <button
              onClick={startExam}
              disabled={!provider || !level}
              className="group w-full sm:w-auto min-h-[48px] inline-flex items-center justify-center gap-2.5 px-8 py-3.5 rounded-full bg-stone-900 text-white text-[15px] font-semibold shadow-[0_4px_16px_rgba(28,25,23,0.2)] hover:shadow-[0_6px_24px_rgba(28,25,23,0.25)] hover:bg-stone-800 transition-all duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation"
            >
              {t('tryExam.startCta', { defaultValue: 'Start my free mock exam' })}
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </button>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-5">
              {[
                t('tryExam.chipFormat', { defaultValue: 'Authentic exam format' }),
                t('tryExam.chipSections', { defaultValue: 'Reading · Listening · Writing' }),
                t('tryExam.chipNoCard', { defaultValue: 'No credit card, no signup' }),
              ].map((chip) => (
                <span key={chip} className="inline-flex items-center gap-1.5 text-xs text-stone-400">
                  <Check className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" strokeWidth={2.5} />
                  {chip}
                </span>
              ))}
            </div>

            {!isAuthenticated && (
              <p className="mt-6 pt-5 border-t border-stone-100 text-sm text-stone-500">
                {t('tryExam.haveAccount', { defaultValue: 'Already have an account?' })}{' '}
                <button
                  onClick={() => navigate('/auth?mode=login')}
                  className="font-semibold text-stone-800 underline underline-offset-2 hover:text-black transition-colors duration-200 cursor-pointer"
                >
                  {t('home.nav.login', { defaultValue: 'Log in' })}
                </button>
              </p>
            )}
          </div>
        )}

        {step === 'results' && results && (
          <div className="animate-fade-in-up max-w-lg">
            <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-amber-600 mb-2">
              {t('tryExam.resultsBadge', { defaultValue: 'Your result' })}
            </p>
            <h1
              className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tighter mb-6"
              style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}
            >
              {t('tryExam.resultsTitle', { provider, level, defaultValue: 'You finished the {{provider}} {{level}} exam!' })}
            </h1>

            <div className="rounded-2xl border-2 border-stone-200 bg-white p-6 mb-6 shadow-[0_10px_28px_rgba(15,23,42,0.06)]">
              <p className="text-sm text-stone-500 mb-1">{t('tryExam.overallScore', { defaultValue: 'Overall score' })}</p>
              <p className="text-5xl font-black text-stone-900 mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {Math.round(results.overallScore || 0)}%
              </p>
              <div className="space-y-3">
                {Object.entries(results.sectionScores || {})
                  .filter(([, s]) => (s?.total ?? s?.maxScore ?? 0) > 0)
                  .map(([sectionId, s]) => {
                    const pct = Math.round(s.percentage ?? (((s.score || 0) / (s.total || s.maxScore || 1)) * 100));
                    const Icon = /h(ö|o)r|listen/i.test(sectionId) ? Headphones : /schreib|writ/i.test(sectionId) ? PenLine : FileText;
                    return (
                      <div key={sectionId}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="inline-flex items-center gap-2 font-semibold text-stone-700 capitalize">
                            <Icon className="w-4 h-4 text-stone-400" />
                            {sectionId}
                          </span>
                          <span className="font-bold text-stone-900">{pct}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                          <div className="h-full rounded-full bg-[#3A86FF] transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {isAuthenticated && user ? (
              <div className="rounded-2xl border-2 border-stone-900 bg-stone-900 text-white p-6 mb-6">
                <p className="text-sm text-stone-300 leading-relaxed mb-4">
                  {t('tryExam.dashboardText', { defaultValue: 'Head to your dashboard for your study plan, more mock exams, and AI writing feedback.' })}
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="group w-full min-h-[48px] inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full bg-white text-stone-900 text-[15px] font-semibold hover:bg-stone-100 transition-all duration-200 cursor-pointer touch-manipulation"
                >
                  {t('tryExam.dashboardCta', { defaultValue: 'Go to my dashboard' })}
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>
              </div>
            ) : (
              /* Signup gate: detailed feedback lives behind a free account */
              <div className="rounded-2xl border-2 border-stone-900 bg-stone-900 text-white p-6 mb-6">
                <p className="inline-flex items-center gap-2 text-sm font-semibold text-amber-400 mb-2">
                  <Lock className="w-4 h-4" />
                  {t('tryExam.unlockBadge', { defaultValue: 'See what you got wrong' })}
                </p>
                <p className="text-sm text-stone-300 leading-relaxed mb-4">
                  {t('tryExam.unlockText', { defaultValue: 'Create a free account to review every answer with explanations, get AI feedback on your writing, and unlock your personal study plan.' })}
                </p>
                <button
                  onClick={() => goSignup('guest_results_primary')}
                  className="group w-full min-h-[48px] inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full bg-white text-stone-900 text-[15px] font-semibold hover:bg-stone-100 transition-all duration-200 cursor-pointer touch-manipulation"
                >
                  {t('tryExam.signupCta', { defaultValue: 'Create free account & see my answers' })}
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </button>
              </div>
            )}

            <button
              onClick={() => { setResults(null); setExamData(null); setResumeUid(null); setStep('select'); }}
              className="min-h-[44px] inline-flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors duration-200 cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('tryExam.tryAnother', { defaultValue: 'Try another exam' })}
            </button>
          </div>
        )}
          </div>
        </motion.div>
      </div>
      {/* /try had zero <a href> of any kind: the free-trial entry point was a crawl
          dead end that nothing could be followed out of. */}
      <MarketingFooter />
    </div>
  );
};

export default TryExamPage;
